/**
 * ListenButton - Reusable button for replaying audio prompts
 *
 * Displays a speaker icon that allows kids to replay the audio instruction.
 * Used in PreK and K Assessment screens.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Volume2 } from 'lucide-react';

interface ListenButtonProps {
  onClick: () => void;
  disabled: boolean;
  isPlaying: boolean;
}

const ListenButton: React.FC<ListenButtonProps> = ({
  onClick,
  disabled,
  isPlaying,
}) => {
  const isDisabled = disabled || isPlaying;

  return (
    <motion.button
      onClick={onClick}
      disabled={isDisabled}
      whileHover={!isDisabled ? { scale: 1.1 } : {}}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
      aria-label="Listen to instructions"
      aria-disabled={isDisabled}
      className={`
        w-14 h-14 rounded-full shadow-lg flex items-center justify-center
        transition-all duration-200
        focus:outline-none focus:ring-4 focus:ring-blue-300
        ${
          isDisabled
            ? 'bg-gray-300 cursor-not-allowed opacity-60'
            : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 cursor-pointer'
        }
        ${isPlaying ? 'animate-pulse' : ''}
      `}
    >
      <Volume2 className="w-7 h-7 text-white" />
    </motion.button>
  );
};

export default ListenButton;
